import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GlobalTable } from "../../../global/components/GlobalTable";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import useGet from "../../../global/hooks/useGet";
import { AuditLogColumns } from "./Columns";
import { AuditLogFilter } from "../Components/FilterComponent";

const auditTimeToUnix = (value) => {
  if (value && typeof value.unix === "function") {
    return value.unix();
  }
  const asNumber = Number(value);
  if (Number.isFinite(asNumber) && String(value).trim() !== "" && !String(value).includes("-")) {
    return asNumber;
  }
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? value : Math.floor(ms / 1000);
};

const initialFilterState = {
  AuditNo: "",
  AuditTime: "",
  AccountUserId: "",
  Description: "",
};

export const AuditLog = () => {
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "AuditNo",
    order: "desc",
  });
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {};

  const [pagination, setPagination] = useState({
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  });
  const [filter, setFilter] = useState(initialFilterState);

  const query = new URLSearchParams(params);
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();

  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/audits/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/audits/`;
  const { data, loading, refetch } = useGet(url);

  useEffect(() => {
    const matchedFilter = { ...initialFilterState };
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined && modParams[key] !== "") {
        matchedFilter[key] = modParams[key];
      }
    }
    setFilter(matchedFilter);
  }, []);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.AuditNo,
      }));
      setListData(newData || []);
    }
  }, [data, loading]);

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize,
      });
      setParams({
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page,
      });
    },
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""']/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = () => {
    const filterDict = {};
    for (const key in filter) {
      const value = filter[key];
      if (value !== null && value !== "" && value !== undefined) {
        if (key === "AuditTime") {
          filterDict[key] = auditTimeToUnix(value);
        } else {
          filterDict[key] = value;
        }
      }
    }

    if (Object.keys(filterDict).length === 0) {
      handleReset();
      return;
    }

    setPagination((prev) => ({ ...prev, current: 1 }));
    setParams({
      limit: 10,
      sort_by: "AuditNo",
      order: "desc",
      page: 1,
      ...filterDict,
    });
    refetch();
  };

  const handleReset = () => {
    setFilter({ ...initialFilterState });
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    setParams({
      limit: 10,
      sort_by: "AuditNo",
      order: "desc",
    });
    refetch();
  };

  const breadcrumbItems = [
    {
      label: "Audit Log",
      href: "/audit-log",
    },
  ];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <AuditLogFilter
          filter={filter}
          handleFilterChange={handleFilterChange}
          handleFilter={handleFilter}
          handleReset={handleReset}
          setFilter={setFilter}
          params={params}
        />
        <GlobalTable
          columns={AuditLogColumns(setParams, params)}
          dataSource={listData}
          loading={loading}
          pagination={paginationConfig}
          resize
        />
      </div>
    </div>
  );
};
